/**
 * Mini Cart Dropdown
 * Shows cart summary after add-to-cart on desktop
 * Fully takes over add-to-cart behavior on desktop to prevent redirects
 */

class MiniCartDropdown {
  constructor() {
    this.dropdown = document.getElementById('mini-cart-dropdown');
    this.itemsContainer = document.getElementById('mini-cart-items');
    this.closeBtn = document.getElementById('mini-cart-close');
    this.progressBar = document.getElementById('mini-cart-progress');
    this.countEl = document.getElementById('mini-cart-count');
    
    this.autoCloseTimer = null;
    this.autoCloseDelay = 5000; // 5 seconds
    this.isDesktop = window.innerWidth >= 990;
    this.isPaused = false;
    
    if (!this.dropdown) return;
    
    this.init();
  }

  init() {
    // Only intercept on desktop
    if (this.isDesktop) {
      this.interceptAddToCart();
    }
    
    // Close button
    this.closeBtn?.addEventListener('click', () => this.hide());
    
    // Click outside to close
    document.addEventListener('click', (e) => {
      if (this.dropdown.classList.contains('is-visible') && 
          !this.dropdown.contains(e.target) && 
          !e.target.closest('#cart-icon-bubble') &&
          !e.target.closest('product-form') &&
          !e.target.closest('[data-product-form]') &&
          !e.target.closest('form[action*="/cart/add"]')) {
        this.hide();
      }
    });

    // Pause auto-close on hover
    this.dropdown.addEventListener('mouseenter', () => {
      this.pauseAutoClose();
    });

    this.dropdown.addEventListener('mouseleave', () => {
      this.resumeAutoClose();
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dropdown.classList.contains('is-visible')) {
        this.hide();
      }
    });

    // Listen for resize to update desktop status
    window.addEventListener('resize', () => {
      this.isDesktop = window.innerWidth >= 990;
    });
  }

  interceptAddToCart() {
    // Capture phase to intercept before other handlers
    document.addEventListener('submit', (e) => {
      const form = e.target;
      
      // Check if it's a product add-to-cart form
      const isAddToCartForm = form.action?.includes('/cart/add') || 
                              form.closest('product-form') ||
                              form.querySelector('[name="add"]');
      
      if (!isAddToCartForm) return;
      
      // Only intercept on desktop
      if (!this.isDesktop) return;

      // Stop the form from submitting normally
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Handle add to cart ourselves
      this.addToCart(form);
      
    }, true); // Capture phase

    // Also intercept click on submit buttons directly
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button[type="submit"], button[name="add"], .product-form__submit');
      if (!btn) return;
      
      const form = btn.closest('form');
      if (!form) return;
      
      const isAddToCartForm = form.action?.includes('/cart/add') || 
                              form.closest('product-form');
      
      if (!isAddToCartForm) return;
      if (!this.isDesktop) return;

      // Stop the click
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Handle add to cart ourselves
      this.addToCart(form);
      
    }, true); // Capture phase
  }

  async addToCart(form) {
    const submitBtn = form.querySelector('[type="submit"], [name="add"], .product-form__submit');
    const originalBtnText = submitBtn?.innerHTML;
    
    try {
      // Show loading state
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span>Adding...</span>';
      }

      const formData = new FormData(form);
      
      // Add to cart via AJAX
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Add to cart failed');
      }

      // Fetch updated cart
      const cart = await this.fetchCart();
      
      // Update cart icon bubble count
      this.updateCartBubble(cart.item_count);
      
      // Render and show dropdown
      this.render(cart);
      this.show();

      // Publish cart update event for other components
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart' });
      }

      // Restore button with success state
      if (submitBtn) {
        submitBtn.innerHTML = '<span>Added! ✓</span>';
        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalBtnText;
        }, 1500);
      }

    } catch (error) {
      console.error('Mini cart error:', error);
      
      // Show error
      if (submitBtn) {
        submitBtn.innerHTML = `<span>${error.message || 'Error'}</span>`;
        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalBtnText;
        }, 2000);
      }
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  updateCartBubble(count) {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (bubble) {
      const countSpan = bubble.querySelector('span[aria-hidden]');
      if (countSpan) {
        countSpan.textContent = count;
      }
    } else if (count > 0) {
      // Create bubble if it doesn't exist
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon) {
        const newBubble = document.createElement('div');
        newBubble.className = 'cart-count-bubble';
        newBubble.innerHTML = `<span aria-hidden="true">${count}</span>`;
        cartIcon.appendChild(newBubble);
      }
    }

    // Also swap icon from empty to full if needed
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && count > 0) {
      const emptyIcon = cartIcon.querySelector('.icon-cart-empty');
      if (emptyIcon) {
        // Replace with filled cart icon
        emptyIcon.outerHTML = `<svg class="icon icon-cart" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none">
          <path fill="currentColor" fill-rule="evenodd" d="M20.5 6.5a4.75 4.75 0 00-4.75 4.75v.56h-3.16l-.77 11.6a5 5 0 004.99 5.34h7.38a5 5 0 004.99-5.33l-.77-11.6h-3.16v-.57A4.75 4.75 0 0020.5 6.5zm3.75 5.31v-.56a3.75 3.75 0 10-7.5 0v.56h7.5zm-7.5 1h7.5v.56a3.75 3.75 0 11-7.5 0v-.56zm-1 0v.56a4.75 4.75 0 109.5 0v-.56h2.22l.71 10.67a4 4 0 01-3.99 4.27h-7.38a4 4 0 01-4-4.27l.72-10.67h2.22z"/>
        </svg>`;
      }
    }
  }

  render(cart) {
    // Update item count
    if (this.countEl) {
      this.countEl.textContent = `(${cart.item_count})`;
    }
    
    if (cart.items.length === 0) {
      this.itemsContainer.innerHTML = `
        <div class="mini-cart-dropdown__empty">
          Your cart is empty
        </div>
      `;
      return;
    }

    const itemsHtml = cart.items.map(item => `
      <div class="mini-cart-dropdown__item">
        <img 
          src="${item.image ? this.getSizedImageUrl(item.image, '120x120') : '//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'}" 
          alt="${item.title}"
          class="mini-cart-dropdown__item-image"
        >
        <div class="mini-cart-dropdown__item-details">
          <p class="mini-cart-dropdown__item-title">${item.product_title}</p>
          ${item.variant_title ? `<p class="mini-cart-dropdown__item-variant">${item.variant_title}</p>` : ''}
          <div class="mini-cart-dropdown__item-bottom">
            <span class="mini-cart-dropdown__item-qty">Qty: ${item.quantity}</span>
            <span class="mini-cart-dropdown__item-price">${this.formatMoney(item.final_line_price)}</span>
          </div>
        </div>
      </div>
    `).join('');

    this.itemsContainer.innerHTML = itemsHtml;
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';
    return url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d+)\./, '.').replace(/\.([^.]+)$/, `_${size}.$1`);
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  show() {
    // Reset states
    this.dropdown.classList.remove('is-closing');
    this.dropdown.classList.remove('is-visible');
    this.closeBtn?.classList.remove('is-spinning');
    this.isPaused = false;
    
    // Reset progress bar to full
    if (this.progressBar) {
      this.progressBar.classList.remove('is-running');
      this.progressBar.style.transform = 'scaleX(1)';
    }
    
    // Force reflow to reset all animations
    this.dropdown.offsetHeight;
    
    // Start progress bar depletion
    if (this.progressBar) {
      this.progressBar.classList.add('is-running');
    }
    
    this.dropdown.classList.add('is-visible');
    this.startAutoClose();
  }

  hide() {
    this.clearAutoClose();
    
    // Stop progress bar
    if (this.progressBar) {
      this.progressBar.classList.remove('is-running');
      this.progressBar.style.transform = 'scaleX(1)';
    }
    
    // Add closing animation
    this.dropdown.classList.add('is-closing');
    
    // Wait for animation to finish, then hide
    setTimeout(() => {
      this.dropdown.classList.remove('is-visible');
      this.dropdown.classList.remove('is-closing');
      this.closeBtn?.classList.remove('is-spinning');
    }, 250);
  }

  // Called when progress bar completes naturally
  triggerAutoClose() {
    // Step 1: Spin the X button
    this.closeBtn?.classList.add('is-spinning');
    
    // Step 2: After spin completes (400ms), close the dropdown
    setTimeout(() => {
      this.hide();
    }, 400);
  }

  startAutoClose() {
    this.clearAutoClose();
    this.autoCloseTimer = setTimeout(() => {
      if (!this.isPaused) {
        this.triggerAutoClose();
      }
    }, this.autoCloseDelay);
  }

  pauseAutoClose() {
    this.isPaused = true;
    this.clearAutoClose();
    
    // Pause the CSS animation
    if (this.progressBar) {
      this.progressBar.style.animationPlayState = 'paused';
    }
  }

  resumeAutoClose() {
    this.isPaused = false;
    
    // Resume the CSS animation
    if (this.progressBar) {
      this.progressBar.style.animationPlayState = 'running';
    }
    
    // Calculate remaining time based on progress bar scaleX
    if (this.progressBar) {
      const computedStyle = window.getComputedStyle(this.progressBar);
      const transform = computedStyle.transform;
      // Parse scaleX from matrix (matrix(scaleX, 0, 0, scaleY, 0, 0))
      let scaleX = 1;
      if (transform && transform !== 'none') {
        const matrix = transform.match(/matrix\(([^)]+)\)/);
        if (matrix) {
          const values = matrix[1].split(',').map(v => parseFloat(v.trim()));
          scaleX = values[0] || 1;
        }
      }
      const remainingTime = scaleX * this.autoCloseDelay;
      
      this.autoCloseTimer = setTimeout(() => {
        if (!this.isPaused) {
          this.triggerAutoClose();
        }
      }, remainingTime);
    }
  }

  clearAutoClose() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.miniCartDropdown = new MiniCartDropdown();
});